import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { ActionButton } from 'core/components/action-button';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import addIcon from 'core/assets/svg/ic-add.svg';
import useStyles from '../liability-coverages-styles';

const businessActivityOptions = lookupsJson.farm.businessActivityOptions;

const CHECKBOX_LABELS = {
  salesperson: 'Includes installation, demonstration, or service operations',
  classifiedTeachers: 'Includes corporal punishment',
  otherTeachers: 'Includes corporal punishment'
};

const TEACHER_TYPES = ['classifiedTeachers', 'otherTeachers'];

const getActivityLabel = (type) => businessActivityOptions.find((o) => o.id === type)?.value || type;

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Business activity type is required'),
  description: Yup.string().required('Description is required'),
  namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
});

const FIELD_NAME = 'farmLiabilityCoverage.businessActivities';

const BusinessActivities = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const activities = getIn(values, FIELD_NAME) || [];

  const namedInsuredOptions = [
    ...(values.drivers || []).map((d) => ({ id: d.id, value: `${d.firstName} ${d.lastName}` })),
    ...(values.interestedParties || []).map((p) => ({ id: p.id, value: p.name }))
  ];

  return (
    <div className={classes.sectionContainer}>
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Business activities</div>}
        initialOpen={activities.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={activities.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {activities.map((activity, index) => {
            const checkboxLabel = CHECKBOX_LABELS[activity.type];
            const detailFields = [];
            if (checkboxLabel) {
              detailFields.push({
                name: 'includesSpecificActivities',
                label: checkboxLabel,
                value: activity.includesSpecificActivities ? 'Yes' : 'No',
                size: 12
              });
            }
            detailFields.push({
              name: 'description',
              label: 'Description',
              value: activity.description || 'N/A',
              size: 12
            });
            if (activity.namedInsureds?.length) {
              const insuredNames = activity.namedInsureds
                .map((id) => namedInsuredOptions.find((o) => o.id === id)?.value || id)
                .join(', ');
              detailFields.push({
                name: 'namedInsured',
                label: 'Named insured',
                value: insuredNames,
                size: 12
              });
            }

            return (
              <CollapsibleListItem
                key={`activity-${index}`}
                title={getActivityLabel(activity.type)}
                rightSideValues={[]}
                detailFields={detailFields}
                onDelete={() => {
                  const newActivities = activities.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newActivities);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
                keyPrefix={`activity-${index}`}
              />
            );
          })}

          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                type: '',
                description: '',
                includesSpecificActivities: false,
                namedInsureds: ['']
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const checkboxLabel = CHECKBOX_LABELS[val.type];
                const newActivity = {
                  type: val.type,
                  description: val.description,
                  ...(checkboxLabel && { includesSpecificActivities: val.includesSpecificActivities }),
                  namedInsureds: val.namedInsureds.filter(Boolean)
                };
                setFieldValue(FIELD_NAME, [...activities, newActivity]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm, values: formValues, setFieldValue: setInnerFieldValue }) => {
                const checkboxLabel = CHECKBOX_LABELS[formValues.type];
                const isTeacherType = TEACHER_TYPES.includes(formValues.type);

                return (
                  <Grid container spacing={2}>
                    <Grid item xs={12} container alignItems="center" wrap="nowrap">
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Field
                          xs={12}
                          type="select"
                          id="type"
                          name="type"
                          mode="dark"
                          label="Type of business activity"
                          options={businessActivityOptions}
                          disabled={disabled}
                        />
                      </div>
                      {formValues.type && (
                        <ActionButton
                          onClick={() => setInnerFieldValue('type', '')}
                          className={classes.deleteInsuredButton}
                          icon={deleteIcon}
                          disabled={disabled}
                          size="small"
                        />
                      )}
                    </Grid>
                    {formValues.type && (
                      <>
                        {checkboxLabel && (
                          <Grid item key={formValues.type}>
                            <Field
                              xs={12}
                              type="checkbox"
                              id="includesSpecificActivities"
                              name="includesSpecificActivities"
                              mode="dark"
                              label={
                                isTeacherType ? (
                                  <>
                                    {checkboxLabel}
                                    <br />
                                    <span className={classes.checkboxDescription}>
                                      If included, medical payments are not included for teachers.
                                    </span>
                                  </>
                                ) : (
                                  checkboxLabel
                                )
                              }
                              disabled={disabled}
                            />
                          </Grid>
                        )}
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                        {formValues.namedInsureds.map((_, index) => (
                          <Grid item xs={12} key={`insured-${index}`} container alignItems="center" wrap="nowrap">
                            <div style={{ flexGrow: 1 }}>
                              <Field
                                xs={12}
                                type="select"
                                id={`namedInsureds.${index}`}
                                name={`namedInsureds.${index}`}
                                mode="dark"
                                label={`Named insured ${String(index + 1).padStart(2, '0')}`}
                                options={namedInsuredOptions}
                                disabled={disabled}
                              />
                            </div>
                            {index > 0 && (
                              <ActionButton
                                onClick={() => {
                                  const newInsureds = formValues.namedInsureds.filter((_, i) => i !== index);
                                  setInnerFieldValue('namedInsureds', newInsureds);
                                }}
                                className={classes.deleteInsuredButton}
                                icon={deleteIcon}
                                disabled={disabled}
                                size="small"
                              />
                            )}
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            className={classes.addAnotherButton}
                            onClick={() => setInnerFieldValue('namedInsureds', [...formValues.namedInsureds, ''])}
                            disabled={disabled}
                            startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                          >
                            Add another named insured
                          </Button>
                        </Grid>
                        <Grid container alignItems="center" className={classes.addButtonRow}>
                          <Button
                            type="button"
                            mode="big"
                            variant="contained"
                            color="secondary"
                            className={classes.addButton}
                            disabled={disabled}
                            onClick={submitForm}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

BusinessActivities.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default BusinessActivities;
